import React, { useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';

import { ABSENCE_REASONS, AbsenceReason, Absence, UNPAID_REASONS } from '../../../models/monthly-declaration.model';
import { DeclarationDA } from '../../../services/admin/declaration.service';
import { countSwissdecDays } from '../../../scripts/swissdec-days';
import { GlobalSwal } from '../../../styles/swal.style';
import { modalShow, modalHide } from '../../../utils/modal.util';

const MODAL_ID = 'modal-absence';

const REASON_LABEL_MAP: Record<AbsenceReason, string> = {
  illness: 'absenceReasonIllness',
  accident_occupational: 'absenceReasonAccidentOccupational',
  accident_non_occupational: 'absenceReasonAccidentNonOccupational',
  military_service: 'absenceReasonMilitaryService',
  maternity: 'absenceReasonMaternity',
  paternity: 'absenceReasonPaternity',
  unpaid: 'absenceReasonUnpaid',
  unjustified: 'absenceReasonUnjustified',
};

export function absenceReasonLabel(lang: any, reason: string): string {
  const key = REASON_LABEL_MAP[reason as AbsenceReason];
  return key ? (lang.labels[key] ?? reason) : reason;
}

interface AbsenceModalProps {
  companyId: string;
  employeeId: string;
  lang: any;
  absence?: Absence | null;
  onSuccess: () => void;
  onClose: () => void;
}

const AbsenceModal: React.FunctionComponent<AbsenceModalProps> = ({
  companyId, employeeId, lang, absence, onSuccess, onClose,
}) => {
  const schema = useMemo(() => z.object({
    reason: z.enum(ABSENCE_REASONS as any as [string, ...string[]]),
    from: z.string().min(1),
    to: z.string().optional().nullable(),
    incapacity_rate: z.number().min(1).max(100),
    salary_continuation_rate: z.number().min(0).max(100),
    notes: z.string().optional().nullable(),
  }), []);

  const { register, handleSubmit, watch, setValue, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      reason: absence?.reason ?? ('illness' as string),
      from: absence?.from?.substring(0, 10) ?? '',
      to: absence?.to?.substring(0, 10) ?? '',
      incapacity_rate: absence?.incapacity_rate ?? 100,
      salary_continuation_rate: absence?.salary_continuation_rate ?? 100,
      notes: absence?.notes ?? '',
    },
  });

  useEffect(() => {
    modalShow(MODAL_ID);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reset({
      reason: absence?.reason ?? ('illness' as string),
      from: absence?.from?.substring(0, 10) ?? '',
      to: absence?.to?.substring(0, 10) ?? '',
      incapacity_rate: absence?.incapacity_rate ?? 100,
      salary_continuation_rate: absence?.salary_continuation_rate ?? 100,
      notes: absence?.notes ?? '',
    });
  }, [absence, reset]);

  const watchReason = watch('reason');
  const watchFrom = watch('from');
  const watchTo = watch('to');
  const isUnpaid = UNPAID_REASONS.includes(watchReason as AbsenceReason);

  useEffect(() => {
    if (isUnpaid) {
      setValue('salary_continuation_rate', 0);
    }
  }, [isUnpaid, setValue]);

  const daysRetained = useMemo(() => {
    if (!watchFrom || !watchTo) return null;
    return countSwissdecDays(watchFrom, watchTo);
  }, [watchFrom, watchTo]);

  const closeModal = useCallback(() => {
    modalHide(MODAL_ID);
    onClose();
  }, [onClose]);

  const onSubmit = async (data: any) => {
    const body = {
      reason: data.reason,
      from: data.from,
      to: data.to || null,
      incapacity_rate: data.incapacity_rate,
      salary_continuation_rate: isUnpaid ? 0 : data.salary_continuation_rate,
      notes: data.notes || null,
    };

    let response;
    if (absence) {
      response = await DeclarationDA.updateAbsence(companyId, employeeId, absence.id, body);
    } else {
      response = await DeclarationDA.createAbsence(companyId, employeeId, body);
    }

    if (response.status === 200 || response.status === 201) {
      toast.success(lang.labels.actionSuccessful || 'Success');
      closeModal();
      onSuccess();
    } else {
      toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
    }
  };

  const handleDelete = async () => {
    if (!absence) return;
    const result = await GlobalSwal.fire({
      title: lang.labels.deleteAbsence,
      text: lang.labels.confirmDeleteAbsence,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.cancel,
    });
    if (!result.isConfirmed) return;

    const response = await DeclarationDA.deleteAbsence(companyId, employeeId, absence.id);
    if (response.status === 200) {
      toast.success(lang.labels.actionSuccessful || 'Success');
      closeModal();
      onSuccess();
    } else {
      toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
    }
  };

  return (
    <div className="modal fade" id={MODAL_ID} tabIndex={-1} data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bolder">
              {absence ? lang.labels.editAbsence : lang.labels.addAbsence}
            </h5>
            <button type="button" className="btn-close" onClick={closeModal} disabled={isSubmitting}></button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body">
              <div className="row mb-4">
                <div className="col-md-4">
                  <label className="form-label fw-bold">{lang.labels.absenceReason}</label>
                  <select className={`form-select form-select-sm ${errors.reason ? 'is-invalid' : ''}`} {...register('reason')}>
                    {ABSENCE_REASONS.map(r => (
                      <option key={r} value={r}>{absenceReasonLabel(lang, r)}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">{lang.labels.absenceFrom}</label>
                  <input type="date" className={`form-control form-control-sm ${errors.from ? 'is-invalid' : ''}`} {...register('from')} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">{lang.labels.absenceTo}</label>
                  <input type="date" className={`form-control form-control-sm ${errors.to ? 'is-invalid' : ''}`} {...register('to')} />
                  <span className="form-text text-muted fs-8">{lang.labels.absenceToHelp}</span>
                </div>
              </div>

              {daysRetained !== null && daysRetained > 0 && (
                <div className="alert alert-info py-2 mb-4 d-flex align-items-center">
                  <i className="bi bi-calendar-check me-2"></i>
                  <span className="fw-bold">
                    {(lang.labels.daysRetained || '{days} day(s) retained').replace('{days}', String(daysRetained))}
                  </span>
                  <span className="text-muted ms-2 fs-8">(Swissdec 30/360)</span>
                </div>
              )}

              <div className="row mb-4">
                <div className="col-md-4">
                  <label className="form-label fw-bold">{lang.labels.incapacityRate} (%)</label>
                  <input
                    type="number"
                    min={1} max={100}
                    className={`form-control form-control-sm ${errors.incapacity_rate ? 'is-invalid' : ''}`}
                    {...register('incapacity_rate', { valueAsNumber: true })}
                  />
                </div>
                {!isUnpaid && (
                  <div className="col-md-4">
                    <label className="form-label fw-bold">{lang.labels.salaryContinuationRate} (%)</label>
                    <input
                      type="number"
                      min={0} max={100}
                      className={`form-control form-control-sm ${errors.salary_continuation_rate ? 'is-invalid' : ''}`}
                      {...register('salary_continuation_rate', { valueAsNumber: true })}
                    />
                  </div>
                )}
                <div className="col-md-4">
                  <label className="form-label fw-bold">{lang.labels.notes}</label>
                  <input type="text" className="form-control form-control-sm" {...register('notes')} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {absence && (
                <button type="button" className="btn btn-sm btn-light-danger me-auto" onClick={handleDelete} disabled={isSubmitting}>
                  <i className="bi bi-trash me-1"></i>{lang.labels.deleteAbsence}
                </button>
              )}
              <button type="button" className="btn btn-sm btn-light" onClick={closeModal} disabled={isSubmitting}>
                {lang.labels.cancel}
              </button>
              <button type="submit" className="btn btn-sm btn-primary" disabled={isSubmitting}>
                {isSubmitting
                  ? <span className="spinner-border spinner-border-sm me-1"></span>
                  : <i className="bi bi-check-lg me-1"></i>
                }
                {lang.labels.saveDeclaration}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AbsenceModal;
